import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Callout, ConfirmationModal, TextField, Tooltip, Typography, useOverlayContext } from 'seagull';
import { RichTextEditor } from 'seagull/components/RichTextEditor/RichTextEditor';
import { getTestIds } from 'seagull/utils/TestIds';
import { validateLegalTermsContent, validateLegalTermsName } from '~/business-logic/validators';
import { useLocaleKeys } from '~/i18n';
import { useLegalTerms } from '../../../hooks/reactQuery/queries/useLegalTerms';
import { QueryKeys } from '../../../hooks/reactQuery/queryKeys';
import { ErrorResponse, ServerError } from '../../../infra/ErrorResponse';
import { handleRequest } from '../../../infra/utils';
import { useStoreActions } from '../../../store/store-context';
import { PropagationModal } from '../PropagationModal/PropagationModal';
import styles from './EditDocumentModal.module.scss';
export const { EditDocumentModalTID } = getTestIds();
export const EditDocumentModal = (props)=>{
    const { t: texts } = useLocaleKeys('components.legalTermsDropdown');
    const { t: commonButtons } = useLocaleKeys('common.buttons');
    const { t: commonToasts } = useLocaleKeys('common.toasts');
    const { variant, legalTermsId, initialName = '', initialContent, usedIn, onLocalSave, dismissModal } = props;
    const getTitle = (v)=>{
        if (v.type === 'global') {
            return texts.editDocumentTitle();
        }
        return v.context === 'proposal' ? texts.editDocumentForProposal() : texts.editDocumentForAgreement();
    };
    const getInfoBannerText = (v)=>{
        if (v.type === 'global') {
            return texts.infoBannerGlobal();
        }
        return v.context === 'proposal' ? texts.infoBannerProposal() : texts.infoBannerAgreement();
    };
    const getSaveButtonLabel = (v)=>{
        if (v.type === 'global') {
            return commonButtons.save();
        }
        return v.context === 'proposal' ? texts.saveForProposal() : texts.saveForAgreement();
    };
    const isLocalVariant = variant.type === 'local';
    const queryClient = useQueryClient();
    const { api } = useStoreActions();
    const { legalTerms } = useLegalTerms();
    const [name, setName] = useState(initialName);
    const [error, setError] = useState(undefined);
    const [content, setContent] = useState(initialContent);
    const [isSaving, setIsSaving] = useState(false);
    const [showPropagationModal, setShowPropagationModal] = useState(false);
    const { showErrorToast, showSuccessToast } = useOverlayContext();
    const shouldShowPropagation = variant.type === 'global' && usedIn && usedIn.length > 0;
    const existingNames = useMemo(()=>new Set(legalTerms.filter((term)=>term.name.toLowerCase() !== initialName.toLowerCase()).map((term)=>term.name.toLowerCase())), [
        legalTerms,
        initialName
    ]);
    const isDuplicateName = existingNames.has(name.trim().toLowerCase());
    const nameResult = validateLegalTermsName(name);
    const isNameEmpty = !nameResult.valid && nameResult.error === 'empty';
    const isNameTooLong = !nameResult.valid && nameResult.error === 'tooLong';
    const isContentTooLong = !validateLegalTermsContent(content).valid;
    const hasNameError = !!error || isDuplicateName || isNameTooLong;
    const hasContentError = isContentTooLong;
    const hasError = hasNameError || hasContentError;
    const isNameChanged = name !== initialName;
    const isContentChanged = JSON.stringify(content === null || content === void 0 ? void 0 : content.value) !== JSON.stringify(initialContent === null || initialContent === void 0 ? void 0 : initialContent.value);
    const hasChanges = isNameChanged || isContentChanged;
    const nameHelperText = useMemo(()=>{
        if (isDuplicateName || error === ServerError.LegalTermsNameAlreadyExists) {
            return texts.duplicateNameError();
        } else if (isNameTooLong) {
            return texts.nameTooLongError();
        }
    }, [
        isDuplicateName,
        isNameTooLong,
        error
    ]);
    const contentHelperText = isContentTooLong ? texts.contentTooLongError() : undefined;
    const saveWithPropagation = async (draftsFlag, templatesFlag)=>{
        setIsSaving(true);
        const contentString = content ? JSON.stringify(content.value) : '';
        const result = await handleRequest(api.business.updateQuillLegalTerms(legalTermsId, {
            name: name.trim(),
            content: contentString,
            updateRelationshipDrafts: draftsFlag,
            updateProposalTemplates: templatesFlag
        }));
        if (!result.success) {
            if (result.error instanceof ErrorResponse && result.error.hasServerError(ServerError.LegalTermsNameAlreadyExists)) {
                setError(ServerError.LegalTermsNameAlreadyExists);
            } else {
                showErrorToast(commonToasts.anUnknownErrorHasOccurred());
            }
            setIsSaving(false);
            setShowPropagationModal(false);
            return;
        }
        showSuccessToast(commonToasts.documentUpdatedSuccessfully());
        dismissModal();
        queryClient.invalidateQueries({
            queryKey: [
                QueryKeys.LegalTerms
            ]
        });
    };
    const handleSave = async ()=>{
        if (hasError || isNameEmpty) {
            return;
        }
        if (isLocalVariant && onLocalSave && content) {
            onLocalSave(content);
            showSuccessToast(commonToasts.documentUpdatedSuccessfully());
            dismissModal();
            return;
        }
        if (shouldShowPropagation) {
            setShowPropagationModal(true);
            return;
        }
        await saveWithPropagation(false, false);
    };
    const handlePropagationConfirm = async (flags)=>{
        await saveWithPropagation(flags.updateRelationshipDrafts, flags.updateProposalTemplates);
    };
    const handlePropagationCancel = ()=>{
        setShowPropagationModal(false);
    };
    const nameField = /*#__PURE__*/ _jsx(TextField, {
        bi: {
            name: 'document_name'
        },
        collapseHelperText: true,
        testId: EditDocumentModalTID.NameInput(),
        label: texts.editDocumentNameLabel(),
        value: name,
        onChange: (e)=>{
            setName(e.target.value);
            setError(undefined);
        },
        error: hasNameError,
        helperText: nameHelperText,
        helperTextReserveSpace: true,
        width: "100%",
        disabled: isLocalVariant
    });
    if (showPropagationModal && usedIn) {
        return /*#__PURE__*/ _jsx(PropagationModal, {
            usedIn: usedIn,
            isSaving: isSaving,
            onConfirm: handlePropagationConfirm,
            onCancel: handlePropagationCancel
        });
    }
    return /*#__PURE__*/ _jsx(ConfirmationModal, {
        bi: {
            primaryButton: {
                name: 'edit_document_confirm'
            },
            secondaryButton: {
                name: 'edit_document_cancel'
            }
        },
        title: getTitle(variant),
        width: "800px",
        height: "fit-content",
        testId: EditDocumentModalTID.Root(),
        textAlignCenter: false,
        content: /*#__PURE__*/ _jsxs("div", {
            className: styles.contentWrapper,
            children: [
                /*#__PURE__*/ _jsx(Callout, {
                    intent: "info",
                    testId: EditDocumentModalTID.InfoBanner(),
                    withBorder: true,
                    classes: {
                        wrapper: styles.calloutWrapper
                    },
                    children: getInfoBannerText(variant)
                }),
                isLocalVariant ? /*#__PURE__*/ _jsx(Tooltip, {
                    title: texts.editDocumentTooltip(),
                    placement: "top",
                    children: nameField
                }) : nameField,
                /*#__PURE__*/ _jsxs("div", {
                    className: styles.richTextEditorWrapper,
                    children: [
                        /*#__PURE__*/ _jsx(Typography, {
                            variant: "paragraph2",
                            skin: "secondary",
                            className: styles.editorLabel,
                            children: texts.documentContent()
                        }),
                        /*#__PURE__*/ _jsx(RichTextEditor, {
                            bi: {
                                name: 'edit_legal_terms_document_content'
                            },
                            testId: EditDocumentModalTID.ContentEditor(),
                            editorHeight: 300,
                            initialValue: initialContent,
                            error: hasContentError,
                            helperText: contentHelperText,
                            onChange: (value)=>setContent(value)
                        })
                    ]
                })
            ]
        }),
        primaryButtonLabel: getSaveButtonLabel(variant),
        secondaryButtonLabel: commonButtons.cancel(),
        primaryButtonDisabled: hasError || isNameEmpty || !hasChanges,
        primaryButtonLoading: isSaving,
        onPrimaryButtonClick: handleSave,
        onSecondaryButtonClick: ()=>dismissModal()
    });
};
